import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller.ts"
import { WorkPeriod } from "@/models/work-period.model.ts"
import type { IWorkPeriod } from "@/interfaces/api/work-period.interface.ts"
import { WorkPeriodRepository } from "@/repositories/work-period-repository.ts"
import type { IStoreFunctionsConfig } from "@/store/base-functions/base-store-functions.ts"
import { useWorkPeriodStore } from "@/store/work-periods.store.ts"
import { storeToRefs } from "pinia"
import type { KnownBusinessEntityContact } from "@/models/known-business-entity-contact.model.ts"
import type { Moment } from "moment"
import { ODataQueryBuilder } from "@/builder/odata-query-builder"
import type { Ref } from "vue"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers.ts"

export class WorkPeriodController extends GenericModelController<
  WorkPeriod,
  IWorkPeriod,
  WorkPeriodRepository
> {
  constructor(storeFunctionsConfig?: IStoreFunctionsConfig<WorkPeriod>) {
    let enableSynchronization = false
    if (!storeFunctionsConfig) {
      // If no storeFunctionsConfig is provided, this means we are not not subclassing.
      // We will use the default store functions and enable synchronization handlers.
      const workPeriodStore = useWorkPeriodStore()
      const { workPeriods } = storeToRefs(workPeriodStore)
      storeFunctionsConfig = {
        managed: true,
        collection: workPeriods
      }
      enableSynchronization = true
    }

    super(WorkPeriod, storeFunctionsConfig, new WorkPeriodRepository())

    if (enableSynchronization) {
      this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
        WorkPeriod,
        storeFunctionsConfig.collection,
        undefined,
        undefined,
        false // For testing purposes (so we can add items in the app and see them coming in at planning)
      )
    }
  }

  private fetchedForServiceProvidersAndDates = new Set<string>()
  public async getListForKnownBusinessEntityContactAndDate(
    kbecs: KnownBusinessEntityContact | KnownBusinessEntityContact[],
    date: Moment,
    workPeriodsBeingFetchedCount?: Ref<number>,
    deep: boolean = false
  ) {
    // normalize to array
    const providers = Array.isArray(kbecs) ? kbecs : [kbecs]
    const idsToFetch: string[] = []
    for (const provider of providers) {
      // Managed references can still be unresolved during a date change.
      // An undefined ID becomes an invalid OData filter: owner_id in ().
      if (typeof provider?.id !== "string" || !provider.id) {
        continue
      }
      const key = this.getKey(provider, date)
      if (this.fetchedForServiceProvidersAndDates.has(key)) {
        continue
      }
      idsToFetch.push(provider.id)
      this.fetchedForServiceProvidersAndDates.add(key)
    }

    if (idsToFetch.length === 0) {
      // Already fetched for all service providers
      return
    }

    // default up the in‐flight counter if caller didn't pass their own ref
    if (!workPeriodsBeingFetchedCount) {
      const workPeriodStore = useWorkPeriodStore()
      const { workPeriodsBeingFetchedCount: defaultCount } = storeToRefs(workPeriodStore)
      workPeriodsBeingFetchedCount = defaultCount
    }

    // prepare our two dates
    const todayStr = date.format("YYYY-MM-DD")
    const yesterdayStr = date.clone().subtract(1, "day").format("YYYY-MM-DD")

    const query = new ODataQueryBuilder()
      .in("owner_id", idsToFetch)
      .in("time_start__date", [todayStr, yesterdayStr])
      .deep(deep) // Important, else we will have validation issues on work period update (service provider app)

    // fetch
    workPeriodsBeingFetchedCount.value++
    const result = await this.getList({ query })
    workPeriodsBeingFetchedCount.value--
    return result
  }
  // public async getListForKnownBusinessEntityContactAndDate(
  //   kbec: KnownBusinessEntityContact,
  //   date: Moment,
  //   workPeriodsBeingFetchedCount?: Ref<number>
  // ) {
  //   const key = this.getKey(kbec, date)
  //   if (this.fetchedForServiceProviderAndDates.has(key)) {
  //     return
  //   }
  //   this.fetchedForServiceProviderAndDates.add(key)
  //
  //   // Keep track of the number of work periods currently being fetched
  //   if (!workPeriodsBeingFetchedCount) {
  //     const workPeriodStore = useWorkPeriodStore()
  //     const { workPeriodsBeingFetchedCount: defaultWorkPeriodsBeingFetchedCount } =
  //       storeToRefs(workPeriodStore)
  //
  //     // Default to workPeriodsBeingFetchedCount if none provided from the caller
  //     workPeriodsBeingFetchedCount = defaultWorkPeriodsBeingFetchedCount
  //   }
  //
  //   const todayStr = date.format("YYYY-MM-DD")
  //   const yesterdayStr = date.clone().subtract(1, "day").format("YYYY-MM-DD")
  //   const qb = new QueryBuilder().filter((f: FilterBuilder) =>
  //     f
  //       .and((f) => f.filterExpression("owner_id", "eq", kbec.id))
  //       .and((f) =>
  //         // both date conditions are added inside a single .or() callback
  //         f.or((o) => {
  //           // Any work periods starting today.
  //           o.filterExpression("time_start__date", "eq", todayStr)
  //           // Work periods starting yesterday might cross into today.
  //           o.filterExpression("time_start__date", "eq", yesterdayStr)
  //           return o
  //         })
  //       )
  //   )
  //   workPeriodsBeingFetchedCount.value++
  //   const result = await this.getList({ query: qb.toQuery() })
  //   workPeriodsBeingFetchedCount.value--
  //   return result
  // }

  private getKey(kbec: KnownBusinessEntityContact, date: Moment) {
    return `${kbec.id}-${date.format("YYYY-MM-DD")}`
  }
}
